import { supabase } from "../supabaseClient";
import { sortTeamAssignments } from "./sortTeamAssignments";
import { eventNamesOnSquad } from "./signupAvailability";
import { fetchEventsBySquad, withSquadEventIds } from "./squadEventNames";
import {
  cacheRowsFromCalculateScoresResults,
  mapCalculationCacheToHub,
  scoresObjectForTeam,
  teamCacheIsReady,
} from "./calculationCache";

const fetchCalculationCache = async (tournamentId) => {
  const { data, error } = await supabase
    .from("calculation_cache")
    .select(
      "tournament_id, event_id, team_id, bowler_id, calculation_type, scratch_total, handicap_total, handicap_value, results_version"
    )
    .eq("tournament_id", tournamentId);
  if (error) {
    console.error("Error reading calculation_cache:", error);
    return [];
  }
  return data || [];
};

const applyCacheToTeams = (teams, cacheRows) => {
  const { calculationResults, byTeam } = mapCalculationCacheToHub(cacheRows);
  const nextTeams = teams.map((team) => {
    const cached = byTeam.get(team.id);
    const bowlers = (team.bowlers || []).map((bowler) => {
      const totals = cached?.bowlers?.[bowler.id] || {};
      return {
        ...bowler,
        scratchTotal: totals.scratchTotal || 0,
        handicap: totals.handicap || 0,
        handicapTotal: totals.handicapTotal || 0,
      };
    });
    return {
      ...team,
      bowlers,
      teamTotals: {
        ...team.teamTotals,
        scratchTotal: cached?.scratchTotal || 0,
        handicapTotal: cached?.handicapTotal || 0,
      },
    };
  });
  return { teams: nextTeams, calculationResults };
};

const rebuildStaleTeams = async (tournamentId, teams, cacheRows, resultsVersion) => {
  const stale = teams.filter((team) => !teamCacheIsReady(cacheRows, team, resultsVersion));
  if (stale.length === 0) return cacheRows;

  const requests = stale.flatMap((team) => {
    const scores = scoresObjectForTeam(team);
    if (!team.eventsID || Object.keys(scores).length === 0) return [];
    return [
      {
        calculationType: "team_scratch",
        teamId: team.id,
        eventId: team.eventsID,
        scores,
        gamesPerEvent: team.gamesPerEvent,
      },
      {
        calculationType: "team_handicap",
        teamId: team.id,
        eventId: team.eventsID,
        scores,
        gamesPerEvent: team.gamesPerEvent,
      },
    ];
  });

  if (requests.length === 0) return cacheRows;

  const { data, error } = await supabase.functions.invoke("calculate-scores", {
    body: {
      mode: "bulk",
      tournamentId,
      requests,
    },
  });
  if (error) {
    console.error("Error filling scoring calculation cache:", error);
    return cacheRows;
  }

  const refreshed = await fetchCalculationCache(tournamentId);
  const stillStale = teams.some(
    (team) => !teamCacheIsReady(refreshed, team, resultsVersion)
  );
  if (!stillStale) return refreshed;

  return [
    ...refreshed,
    ...cacheRowsFromCalculateScoresResults(data?.results, resultsVersion),
  ];
};

export const loadScoringHubData = async (tournamentId) => {
  if (!tournamentId) {
    throw new Error("Tournament ID is required");
  }

  const { data: tournament, error: tournamentError } = await supabase
    .from("tournaments")
    .select(
      "id, name, organizationID, handicapBase, handicapPercentage, handicapMax, resultsVersion"
    )
    .eq("id", tournamentId)
    .single();
  if (tournamentError) throw tournamentError;

  const { data: squadsData, error: squadsError } = await supabase
    .from("squads")
    .select(
      "id, squadDate, squadTime, squadLocation, startLane, endLane, blackouts, movementRules"
    )
    .eq("tournamentID", tournamentId)
    .eq("isDeleted", false);
  if (squadsError) throw squadsError;

  const squadIds = (squadsData || []).map((squad) => squad.id);
  const eventsBySquad = squadIds.length
    ? await fetchEventsBySquad(squadIds).catch(() => ({}))
    : {};
  const squadsWithEvents = withSquadEventIds(squadsData || [], eventsBySquad);
  if (squadIds.length === 0) {
    return {
      teams: [],
      tournament,
      squads: [],
      events: [],
      calculationResults: {},
    };
  }

  const { data: teamsData, error: teamsError } = await supabase
    .from("teams")
    .select(
      `
      id,
      teamName,
      squadID,
      eventsID,
      created,
      startingPair,
      startingLane,
      slot,
      recapStoragePath,
      recapMimeType,
      recapFileName,
      recapBoundingBox,
      teamassignments!inner (
        id,
        created,
        bowlerID,
        bowlers (
          id,
          name
        )
      )
    `
    )
    .in("squadID", squadIds)
    .eq("isDeleted", false)
    .order("created", { referencedTable: "teamassignments", ascending: true })
    .order("created", { ascending: true });
  if (teamsError) throw teamsError;

  const teamIds = (teamsData || []).map((team) => team.id);
  const bowlerIds = (teamsData || []).flatMap(
    (team) => team.teamassignments?.map((assignment) => assignment.bowlerID) || []
  );

  const locationIds = [
    ...new Set(
      (squadsData || []).map((squad) => squad.squadLocation).filter(Boolean)
    ),
  ];
  const [
    scoresResult,
    averagesResult,
    verificationResult,
    eventEntriesResult,
    eventsResult,
    cacheResult,
    centersResult,
  ] = await Promise.all([
    teamIds.length
      ? supabase
          .from("scores")
          .select("id, bowlerID, gameNumber, score, teamID")
          .in("teamID", teamIds)
      : Promise.resolve({ data: [], error: null }),
    bowlerIds.length
      ? supabase
          .from("tournamentAverages")
          .select("*")
          .in("bowlerID", bowlerIds)
          .eq("tournamentID", tournamentId)
      : Promise.resolve({ data: [], error: null }),
    supabase
      .from("verificationStates")
      .select("*")
      .eq("tournamentId", tournamentId)
      .in("entityType", ["scores", "averages"]),
    teamIds.length
      ? supabase
          .from("eventEntry")
          .select(
            `
            teamID,
            bowlerID,
            eventsID,
            tournamentEvents (
              id,
              eventName,
              isPrimary
            )
          `
          )
          .in("teamID", teamIds)
      : Promise.resolve({ data: [], error: null }),
    supabase
      .from("tournamentEvents")
      .select(
        "id, eventName, eventAbbreviation, isPrimary, bowlerQuantity, gamesPerEvent, templateId, squadId"
      )
      .eq("tournamentID", tournamentId)
      .eq("isDeleted", false),
    fetchCalculationCache(tournamentId),
    locationIds.length
      ? supabase
          .from("bowlingCenters")
          .select("id, centerName")
          .in("id", locationIds)
      : Promise.resolve({ data: [], error: null }),
  ]);

  if (scoresResult.error) throw scoresResult.error;
  if (averagesResult.error) throw averagesResult.error;
  if (eventEntriesResult.error) throw eventEntriesResult.error;
  if (eventsResult.error) throw eventsResult.error;
  if (centersResult.error) {
    console.warn(
      "Could not load bowling centers for squad labels:",
      centersResult.error
    );
  }
  if (verificationResult.error) {
    console.warn(
      "Could not fetch scoring verification states:",
      verificationResult.error
    );
  }

  const teamScoresMap = new Map();
  (scoresResult.data || []).forEach((score) => {
    if (!teamScoresMap.has(score.teamID)) {
      teamScoresMap.set(score.teamID, []);
    }
    teamScoresMap.get(score.teamID).push(score);
  });

  const eventsData = eventsResult.data || [];
  const centerNameById = new Map(
    (centersResult.error ? [] : centersResult.data || []).map((center) => [
      center.id,
      center.centerName || "",
    ])
  );
  const squadsForHub = squadsWithEvents.map((squad) => ({
    ...squad,
    centerName: centerNameById.get(squad.squadLocation) || "",
    eventNames: eventNamesOnSquad(squad, eventsData),
  }));
  const averagesData = averagesResult.data || [];
  const eventEntriesData = eventEntriesResult.data || [];
  const verificationStates = verificationResult.data || [];

  const teams =
    teamsData?.map((team) => {
      const primaryEvent = eventsData.find(
        (event) => event.id === team.eventsID && event.isPrimary
      );
      const gamesPerEvent = primaryEvent?.gamesPerEvent || 3;
      const bowlers =
        sortTeamAssignments(team.teamassignments).map((assignment) => {
          const bowler = assignment.bowlers;
          const averageData = averagesData.find(
            (avg) => avg.bowlerID === assignment.bowlerID
          );
          const eventEntries =
            eventEntriesData
              .filter(
                (entry) =>
                  entry.teamID === team.id &&
                  entry.bowlerID === assignment.bowlerID
              )
              .map((entry) => {
                const event = eventsData.find((item) => item.id === entry.eventsID);
                return {
                  name: event?.eventName || "Unknown Event",
                  abbreviation: event?.eventAbbreviation || null,
                  isPrimary: event?.isPrimary || false,
                };
              }) || [];
          const sidePotEvents = eventsData
            .filter((event) => !event.isPrimary)
            .map((event) => ({
              id: event.id,
              name: event.eventName,
              gamesPerEvent: event.gamesPerEvent || 1,
            }));
          const scores =
            teamScoresMap
              .get(team.id)
              ?.filter((score) => score.bowlerID === assignment.bowlerID)
              .map((score) => ({
                id: score.id,
                gameNumber: score.gameNumber,
                score: score.score,
              })) || [];
          const bowlerVerification = verificationStates.find(
            (state) =>
              state.entityType === "averages" &&
              state.entityId === assignment.bowlerID
          );
          return {
            id: assignment.bowlerID,
            name: bowler?.name || `Bowler ${assignment.bowlerID}`,
            average:
              averageData?.hasAverage === false
                ? null
                : averageData?.average || null,
            hasAverage: averageData?.hasAverage !== false,
            isVerified: bowlerVerification?.isVerified || false,
            eventEntries,
            sidePotEvents,
            scores,
            scratchTotal: 0,
            handicap: 0,
            handicapTotal: 0,
          };
        }) || [];

      const allScores = teamScoresMap.get(team.id) || [];
      const completedGames = allScores.filter((score) => score.score !== null);
      let status = "pending";
      if (completedGames.length > 0) {
        status =
          completedGames.length === allScores.length ? "completed" : "in-progress";
      }

      const teamVerification = verificationStates.find(
        (state) => state.entityType === "scores" && state.entityId === team.id
      );

      return {
        id: team.id,
        teamName: team.teamName,
        squadID: team.squadID,
        eventsID: team.eventsID,
        created: team.created ?? null,
        startingPair: team.startingPair ?? null,
        startingLane: team.startingLane ?? null,
        slot: team.slot ?? null,
        recapStoragePath: team.recapStoragePath ?? null,
        recapMimeType: team.recapMimeType ?? null,
        recapFileName: team.recapFileName ?? null,
        recapBoundingBox: team.recapBoundingBox ?? null,
        tournamentID: tournamentId,
        isVerified: teamVerification?.isVerified || false,
        verifiedAt: teamVerification?.verifiedAt || null,
        verifiedBy: teamVerification?.verifiedBy || null,
        squad: squadsForHub.find((squad) => squad.id === team.squadID) || null,
        bowlers,
        scores: allScores,
        teamTotals: {
          scratchTotal: 0,
          handicapTotal: 0,
          gamesCompleted: completedGames.length,
          status,
          gamesPerEvent,
        },
        gamesPerEvent,
      };
    }) || [];

  const resultsVersion = Number(tournament.resultsVersion) || 0;
  let cacheRows = cacheResult;
  cacheRows = await rebuildStaleTeams(
    tournamentId,
    teams,
    cacheRows,
    resultsVersion
  );

  const withTotals = applyCacheToTeams(teams, cacheRows);

  return {
    teams: withTotals.teams,
    tournament,
    squads: squadsForHub,
    events: eventsData,
    calculationResults: withTotals.calculationResults,
  };
};
